import * as React from "react";

const containerStyle: React.CSSProperties = {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "30px",
  fontFamily: "'Open Sans', Arial, sans-serif",
  lineHeight: "1.8",
  color: "#444",
  backgroundColor: "#f9f9f9",
  borderRadius: "10px",
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)"
};

const headerStyle: React.CSSProperties = {
  textAlign: "center",
  marginBottom: "40px",
  color: "#004085",
  fontSize: "2.5rem",
  fontWeight: "bold"
};

const sectionStyle: React.CSSProperties = {
  marginBottom: "30px",
  padding: "20px",
  backgroundColor: "#ffffff",
  borderRadius: "8px",
  boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)"
};

const sectionTitleStyle: React.CSSProperties = {
  color: "#0056b3",
  fontSize: "1.5rem",
  marginBottom: "15px",
  borderBottom: "2px solid #e9ecef",
  paddingBottom: "5px"
};

const subsectionTitleStyle: React.CSSProperties = {
  color: "#003366",
  fontSize: "1.2rem",
  marginTop: "20px",
  marginBottom: "10px"
};

const listStyle: React.CSSProperties = {
  paddingLeft: "20px",
  marginBottom: "10px",
  listStyleType: "disc"
};

const addressStyle: React.CSSProperties = {
  fontStyle: "normal",
  marginTop: "10px"
};

const footerTextStyle: React.CSSProperties = {
  textAlign: "center",
  marginTop: "40px",
  color: "#666",
  fontSize: "0.9rem"
};

const highlightTextStyle: React.CSSProperties = {
  backgroundColor: "#fffde7",
  padding: "2px 4px",
  borderRadius: "3px"
};

function LegalPage() {
  return (
    <div style={containerStyle}>
      <h1 style={headerStyle}>Privacy Policy & Terms and Conditions</h1>
      <p style={{ textAlign: "center", marginBottom: "30px" }}>
        <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Privacy Policy</h2>
        <p>
          At <strong>The Graphic Cow Company</strong> ("we," "us," or "our"), we are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website (<a href="https://grafcow.com">grafcow.com</a>) or use our services.
        </p>

        <h3 style={subsectionTitleStyle}>1. Information We Collect</h3>
        <p>We may collect the following types of information:</p>
        <ul style={listStyle}>
          <li><strong>Personal Information:</strong> Name, email address, phone number, billing/shipping address, payment information, and account credentials.</li>
          <li><strong>Transaction Information:</strong> Details about purchases, order history, and preferences.</li>
          <li><strong>Technical Data:</strong> IP address, browser type, device information, operating system, and usage data.</li>
          <li><strong>Cookies and Tracking Technologies:</strong> We use cookies, web beacons, and similar technologies to enhance your experience and analyze trends.</li>
          <li><strong>Communications:</strong> Records of your interactions with our customer service team.</li>
        </ul>

        <h3 style={subsectionTitleStyle}>2. How We Use Your Information</h3>
        <p>We may use the information we collect for the following purposes:</p>
        <ul style={listStyle}>
          <li>To process and fulfill your orders and transactions</li>
          <li>To provide customer support and respond to inquiries</li>
          <li>To send administrative information, order confirmations, and account notifications</li>
          <li>To send marketing and promotional communications (with your consent)</li>
          <li>To improve our website, products, and services</li>
          <li>To prevent fraud and enhance security</li>
          <li>To comply with legal obligations and enforce our policies</li>
        </ul>

        <h3 style={subsectionTitleStyle}>3. Information Sharing and Disclosure</h3>
        <p>We may share your information in the following circumstances:</p>
        <ul style={listStyle}>
          <li><strong>Service Providers:</strong> With third-party vendors who perform services on our behalf (e.g., payment processing, shipping, analytics).</li>
          <li><strong>Legal Requirements:</strong> When required by law or to protect our rights, safety, or property.</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets.</li>
        </ul>
        <p style={highlightTextStyle}>
          We do not sell your personal information to third parties for their marketing purposes.
        </p>

        <h3 style={subsectionTitleStyle}>4. Data Retention</h3>
        <p>We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy, unless a longer retention period is required or permitted by law.</p>

        <h3 style={subsectionTitleStyle}>5. Your Rights and Choices</h3>
        <p>Depending on your jurisdiction, you may have certain rights regarding your personal information:</p>
        <ul style={listStyle}>
          <li>Access, correct, or delete your personal information</li>
          <li>Object to or restrict certain processing activities</li>
          <li>Withdraw consent (where processing is based on consent)</li>
          <li>Receive a copy of your data in a portable format</li>
          <li>Lodge a complaint with a supervisory authority</li>
        </ul>
        <p>To exercise these rights, please contact us at <a href="mailto:privacy@grafcow.com">privacy@grafcow.com</a>.</p>

        <h3 style={subsectionTitleStyle}>6. Cookies and Tracking Technologies</h3>
        <p>We use cookies and similar technologies to:</p>
        <ul style={listStyle}>
          <li>Remember your preferences and settings</li>
          <li>Analyze site traffic and usage patterns</li>
          <li>Deliver targeted advertisements (with your consent)</li>
        </ul>
        <p>You can manage cookie preferences through your browser settings. Note that disabling cookies may affect website functionality.</p>

        <h3 style={subsectionTitleStyle}>7. SMS/Text Messaging Policy</h3>
        <p>
          By providing your phone number and opting in, you expressly consent to receive recurring SMS/text messages from The Graphic Cow Company regarding:
        </p>
        <ul style={listStyle}>
          <li>Order confirmations and shipping updates</li>
          <li>Promotional offers and marketing messages</li>
          <li>Account notifications and service updates</li>
        </ul>
        <p style={highlightTextStyle}>
          Message frequency varies. Message and data rates may apply. Carriers are not liable for delayed or undelivered messages.
        </p>
        <p>
          To opt out, reply <strong>STOP</strong> to any message. For help, reply <strong>HELP</strong>. After opting out, you may receive a final confirmation message.
        </p>
        <p>
          We do not share your phone number or SMS opt-in consent with third parties for their marketing purposes. Your information is used solely for communication between you and The Graphic Cow Company.
        </p>

        <h3 style={subsectionTitleStyle}>8. Security Measures</h3>
        <p>We implement appropriate technical and organizational measures to protect your personal information, including:</p>
        <ul style={listStyle}>
          <li>SSL encryption for data transmission</li>
          <li>Secure payment processing</li>
          <li>Access controls and authentication procedures</li>
          <li>Regular security assessments</li>
        </ul>
        <p>However, no method of transmission over the Internet or electronic storage is 100% secure.</p>

        <h3 style={subsectionTitleStyle}>9. International Data Transfers</h3>
        <p>Your information may be transferred to and processed in countries other than your own, which may have different data protection laws.</p>

        <h3 style={subsectionTitleStyle}>10. Children's Privacy</h3>
        <p>Our services are not directed to individuals under 16. We do not knowingly collect personal information from children without parental consent.</p>

        <h3 style={subsectionTitleStyle}>11. Changes to This Policy</h3>
        <p>We may update this Privacy Policy periodically. The "Last Updated" date at the top of this page indicates when changes were made. Your continued use of our services constitutes acceptance of the updated policy.</p>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Terms and Conditions</h2>
        <p>These Terms and Conditions ("Terms") govern your use of The Graphic Cow Company website and services. By accessing or using our services, you agree to be bound by these Terms.</p>

        <h3 style={subsectionTitleStyle}>1. Account Registration</h3>
        <ul style={listStyle}>
          <li>You must provide accurate and complete registration information.</li>
          <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
          <li>You must notify us immediately of any unauthorized use of your account.</li>
        </ul>

        <h3 style={subsectionTitleStyle}>2. Orders and Payments</h3>
        <ul style={listStyle}>
          <li>All orders are subject to product availability and our acceptance.</li>
          <li>Prices are subject to change without notice.</li>
          <li>We accept various payment methods as indicated during checkout.</li>
          <li>You represent that you have the legal right to use any payment method provided.</li>
        </ul>

        <h3 style={subsectionTitleStyle}>3. Shipping and Returns</h3>
        <ul style={listStyle}>
          <li>Shipping times are estimates and not guaranteed.</li>
          <li>Risk of loss passes to you upon delivery to the carrier.</li>
          <li>Return policies are outlined on our website and may vary by product.</li>
        </ul>

        <h3 style={subsectionTitleStyle}>4. Intellectual Property</h3>
        <ul style={listStyle}>
          <li>All content on our website (text, graphics, logos, etc.) is our property or licensed to us.</li>
          <li>You may not reproduce, distribute, or create derivative works without our express permission.</li>
          <li>Any user-submitted content grants us a perpetual license to use, modify, and display such content.</li>
        </ul>

        <h3 style={subsectionTitleStyle}>5. Prohibited Conduct</h3>
        <p>You agree not to:</p>
        <ul style={listStyle}>
          <li>Use our services for any illegal purpose or in violation of any laws</li>
          <li>Infringe upon the rights of others</li>
          <li>Interfere with the operation of our services</li>
          <li>Attempt to gain unauthorized access to our systems</li>
          <li>Use any automated means to access our services without permission</li>
        </ul>

        <h3 style={subsectionTitleStyle}>6. Disclaimer of Warranties</h3>
        <p style={highlightTextStyle}>
          OUR SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
        </p>
        <p>We do not warrant that our services will be uninterrupted, secure, or error-free.</p>

        <h3 style={subsectionTitleStyle}>7. Limitation of Liability</h3>
        <p style={highlightTextStyle}>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE GRAPHIC COW COMPANY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (A) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICES; (B) ANY CONDUCT OR CONTENT OF ANY THIRD PARTY ON THE SERVICES; OR (C) UNAUTHORIZED ACCESS, USE, OR ALTERATION OF YOUR TRANSMISSIONS OR CONTENT.
        </p>
        <p>Our total liability in any matter arising out of or related to these Terms shall not exceed the amount you paid us in the 12 months preceding the event giving rise to liability.</p>

        <h3 style={subsectionTitleStyle}>8. Indemnification</h3>
        <p>You agree to defend, indemnify, and hold harmless The Graphic Cow Company and its affiliates, officers, directors, employees, and agents from any claims, liabilities, damages, losses, and expenses arising from:</p>
        <ul style={listStyle}>
          <li>Your use of our services</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any third-party right</li>
          <li>Any content you submit or transmit through our services</li>
        </ul>

        <h3 style={subsectionTitleStyle}>9. Termination</h3>
        <p>We may suspend or terminate your access to our services at any time, without notice, for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, or for any other reason.</p>

        <h3 style={subsectionTitleStyle}>10. Governing Law and Dispute Resolution</h3>
        <p>These Terms shall be governed by the laws of the State of South Carolina, without regard to its conflict of laws principles.</p>
        <p>Any disputes arising out of or related to these Terms or our services shall be resolved through binding arbitration in Greenville, SC, in accordance with the rules of the American Arbitration Association.</p>

        <h3 style={subsectionTitleStyle}>11. Changes to These Terms</h3>
        <p>We reserve the right to modify these Terms at any time. We will notify you of material changes through our website or other communication methods. Your continued use of our services constitutes acceptance of the updated Terms.</p>

        <h3 style={subsectionTitleStyle}>12. General Provisions</h3>
        <ul style={listStyle}>
          <li>These Terms constitute the entire agreement between you and The Graphic Cow Company.</li>
          <li>If any provision is found to be unenforceable, the remaining provisions will remain in full effect.</li>
          <li>Our failure to enforce any right or provision will not be deemed a waiver of such right.</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={sectionTitleStyle}>Contact Information</h2>
        <p>If you have any questions about these policies, please contact us:</p>
        <address style={addressStyle}>
          The Graphic Cow Company<br />
          Attn: Legal Department<br />
          400 S Pleasantburg Dr, Suite B<br />
          Greenville, SC 29607<br />
          USA<br />
          Email: <a href="mailto:sales@grafcow.com">sales@grafcow.com</a><br />
          Phone: <a href="tel:+18645555700">(864) 555-7000</a>
        </address>
      </div>

      <p style={footerTextStyle}>
        <small>&copy; {new Date().getFullYear()} The Graphic Cow Company. All rights reserved.</small>
      </p>
    </div>
  );
}

export default LegalPage;